// Route: /audio-recaps — список аудио-резюме.
// MSW: GET /api/audio-recaps.
// См. docs/ui-spec/audio-recaps.list.{desktop,mobile}.md.
//
// Тактильный редизайн «Картотека»: SectionHeader + tilt-карточки recap'ов,
// единые EmptyState/ErrorState, фильтр форматов pill-чипами.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, EmptyState, ErrorState, SectionHeader, Skeleton } from "@studyhub/ui";
import { IconHeadphones, IconPlus } from "@tabler/icons-react";
import { createFileRoute, Link, Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type AudioRecapFormat, useAudioRecapsList } from "@/components/audio-recaps/_api";
import { FormatFilterBar, RecapCard } from "@/components/audio-recaps/recap-card";
import { SECTION_TINT } from "@/components/navigation/section-icons";
import {
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { useTier } from "@/hooks/use-tier";
import { useViewMode } from "@/hooks/use-view-mode";
import { cn } from "@/lib/cn";
import { api } from "@/lib/trpc-client";

const searchSchema = z.object({
	format: z.enum(["podcast", "lecture", "audiobook", "summary"]).optional(),
	material: z.string().optional(),
	q: z.string().optional(),
	demo: z
		.enum(["default", "loading", "empty", "error", "processing", "ready", "partial"])
		.optional(),
});

export const Route = createFileRoute("/audio-recaps/")({
	validateSearch: tolerantSearch(searchSchema),
	component: AudioRecapsListPage,
	pendingComponent: PagePendingFallback,
});

function AudioRecapsListPage(): React.ReactElement {
	const { t } = useTranslation("audio");
	const intro = useSectionIntro("audio-recap");
	const search = useSearch({ from: "/audio-recaps/" });
	const { isFree } = useTier();
	const utils = api.useUtils();
	const query = useAudioRecapsList();
	const navigate = useNavigate();
	const [view, setView] = useViewMode("audio-recaps");

	const setQuery = (value: string): void =>
		void navigate({
			to: "/audio-recaps",
			search: (prev) => ({ ...prev, q: value || undefined }),
		});

	// Real-time: синтез аудио идёт асинхронно (Inngest) — без подписки карточка
	// списка зависала в «В очереди/Синтезируем» до refetch на focus/F5.
	// detail-route ($id) подписан, список — нет. Инвалидируем на всех 3 событиях.
	useSocketEvent("audio-recap:ready", () => void utils.audioRecaps.recaps.list.invalidate());
	useSocketEvent("audio-recap:script_ready", () => void utils.audioRecaps.recaps.list.invalidate());
	useSocketEvent("audio-recap:failed", () => void utils.audioRecaps.recaps.list.invalidate());

	const isLoading = search.demo === "loading" || query.isLoading;
	const isError = search.demo === "error" || query.isError;
	// Cast: routeTree.gen.ts ещё не содержит "/audio-recaps/new" (см. new.tsx).
	const goToWizard = () => void navigate({ to: "/audio-recaps/new" as never });

	// Базовый список (без фильтра по формату) — для подсчёта в pill-чипах.
	const allItems = useMemo(() => {
		if (search.demo === "empty") return [];
		let list = query.data?.items ?? [];
		if (search.q) {
			const q = search.q.toLowerCase();
			list = list.filter((r) => r.title.toLowerCase().includes(q));
		}
		return list;
	}, [query.data, search.demo, search.q]);

	const items = useMemo(() => {
		if (!search.format) return allItems;
		return allItems.filter((r) => r.format === (search.format as AudioRecapFormat));
	}, [allItems, search.format]);

	// Presence-aware: ждём на списке (скелетоны queued/generating) → full mute notify.
	const pendingWatchKeys = useMemo(
		() =>
			allItems
				.filter((r) => r.status === "queued" || r.status.startsWith("generating"))
				.map((r) => markArtifactSeenKey("audio-recap", r.id)),
		[allItems],
	);
	useArtifactWatching(pendingWatchKeys);

	// Audio-recap — фича Plus/Pro. На промо-период все юзеры Pro, поэтому гард
	// не срабатывает; на всякий случай ведём не на отключённый /billing/checkout,
	// а на /settings/billing. Client-side guard ПОСЛЕ всех хуков (REVIEW-5 #12).
	if (isFree) {
		return <Navigate to="/settings/billing" replace />;
	}

	const setFormat = (next: AudioRecapFormat | null): void =>
		void navigate({
			to: "/audio-recaps",
			search: (prev) => ({ ...prev, format: next ?? undefined }),
		});

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				icon={<IconHeadphones />}
				category="ai"
				tint={SECTION_TINT.audioRecap}
				title={t("title")}
				description={t("list.subtitle", {
					count: allItems.length,
					defaultValue: "{{count}} аудио в твоей картотеке",
				})}
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<Button asChild className="pointer-coarse:h-11 pointer-coarse:px-4">
							<Link to={"/audio-recaps/new" as never} aria-label={t("list.create")}>
								<IconPlus size={16} />
								<span className="hidden sm:inline">{t("list.create")}</span>
							</Link>
						</Button>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
				    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{!isError && (allItems.length > 0 || search.q) && (
				<ListToolbar
					search={{
						value: search.q ?? "",
						onChange: setQuery,
						placeholder: t("list.searchPlaceholder", { defaultValue: "Поиск по аудио…" }),
					}}
					segments={
						<FormatFilterBar items={allItems} active={search.format ?? null} onChange={setFormat} />
					}
					view={view}
					onViewChange={setView}
				/>
			)}

			{isLoading ? (
				<ListSkeleton view={view} />
			) : isError ? (
				<ErrorState
					title={t("list.errorTitle")}
					description={t("list.errorDescription", {
						defaultValue: "Проверь соединение и попробуй ещё раз.",
					})}
					action={
						<Button onClick={() => void query.refetch()} variant="outline" size="sm">
							{t("list.retry")}
						</Button>
					}
				/>
			) : items.length === 0 ? (
				<EmptyState
					icon={<IconHeadphones />}
					title={
						search.format
							? t("list.emptyFilterTitle", { defaultValue: "Нет аудио в этом формате" })
							: t("list.emptyTitle")
					}
					description={
						search.format
							? t("list.emptyFilterDescription", {
									defaultValue: "Создай аудио или выбери другой формат фильтра.",
								})
							: t("list.emptyDescription")
					}
					action={
						<Button onClick={goToWizard}>
							<IconPlus size={16} />
							{t("list.create")}
						</Button>
					}
				/>
			) : (
				<div className={view === "grid" ? cn("stagger-enter", LIST_PAGE_GRID) : LIST_PAGE_STACK}>
					{items.map((recap, idx) => (
						<RecapCard
							key={recap.id}
							recap={recap}
							view={view}
							tilt={view === "grid" ? (idx % 2 === 0 ? 1.4 : -1.4) : undefined}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function ListSkeleton({ view }: { view: "grid" | "list" }): React.ReactElement {
	if (view === "list") {
		return (
			<div className={LIST_PAGE_STACK} aria-busy="true">
				{Array.from({ length: 6 }).map((_, i) => (
					<Skeleton key={i} className="h-[72px] rounded-[var(--radius-card)]" />
				))}
			</div>
		);
	}
	return (
		<div className={LIST_PAGE_GRID} aria-busy="true">
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					key={i}
					className="space-y-3 rounded-[var(--radius-card)] border border-border bg-card p-4 shadow-e1"
				>
					<div className="flex items-center justify-between">
						<Skeleton className="h-6 w-24 rounded-full" />
						<Skeleton className="h-6 w-16 rounded-full" />
					</div>
					<Skeleton className="h-5 w-3/4" />
					<Skeleton className="h-5 w-1/2" />
					<Skeleton className="h-9 w-full rounded-[var(--radius-lg)]" />
				</div>
			))}
		</div>
	);
}
